import type { ChangeEvent } from 'react';
import i18n from '~/i18n';
import type { UserRole } from '~/graphql/graphql.ts';
import { type OrderStatus, STATUS_FLOW } from '~/types/orders.ts';

export const cn = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

export function createSyntheticChangeEvent(
  id: string,
  value: string
): ChangeEvent<HTMLInputElement | HTMLTextAreaElement> {
  return {
    target: { value, id, name: id },
    currentTarget: { value, id, name: id },
  } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
}

export function formatDate(dateString: string, locale?: string) {
  const currentLocale = locale || i18n.language || 'en-US';

  return new Date(dateString).toLocaleDateString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString: string, locale?: string) {
  const currentLocale = locale || i18n.language || 'en-US';
  return new Date(dateString).toLocaleString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatCurrency(
  amount: string | number,
  currency = 'USD',
  locale?: string
): string {
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return '0.00';
  }

  const currentLocale = locale || i18n.language || 'en-US';

  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

export function formatNumber(amount: string | number, locale?: string): string {
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return '0.00';
  }

  const currentLocale = locale || i18n.language || 'en-US';

  return new Intl.NumberFormat(currentLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

export function normalizeUserRole(role: string | undefined): UserRole {
  return role === 'SELLER' ? 'SELLER' : 'BUYER';
}

export function formatOrderStatus(status: string, role: UserRole | undefined) {
  if (role === 'SELLER' && status === 'PENDING_SELLER_CONFIRMATION') {
    return 'Pending Your Confirmation';
  }

  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

type OrderForTotal = {
  items: Array<{
    quantity: { value: number };
    price: number;
  }>;
};

export function calculateOrderTotal(order: OrderForTotal): number {
  return order.items.reduce(
    (sum, item) => sum + item.quantity.value * item.price,
    0
  );
}

export function assertDefined<T>(
  value: T,
  message = 'Expected value to be defined'
): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error(message);
  }
}

export function hasReachedStatus(
  current: OrderStatus,
  step: OrderStatus
): boolean {
  const currentIndex = STATUS_FLOW.indexOf(current);
  const stepIndex = STATUS_FLOW.indexOf(step);

  return currentIndex >= stepIndex;
}

interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: string[];
  extensions?: { classification: string };
}

interface GraphQLResponse {
  errors?: GraphQLError[];
}

export interface ClientError extends Error {
  response?: GraphQLResponse;
}

export function extractGraphQLErrorMessages(error: ClientError): string[] {
  const fallbackMessage = error?.message || 'An unknown error occurred';

  if (error?.response?.errors && Array.isArray(error.response.errors)) {
    const messages = error.response.errors
      .map((err: GraphQLError) => err.message)
      .filter(Boolean);

    return messages.length > 0 ? messages : [fallbackMessage];
  }

  return [fallbackMessage];
}
